<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
            class="discounts-report"
            @sort-changed="sortChanged"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div
                class="
                    d-none d-lg-flex
                    align-items-baseline
                    mr-1
                    mb-1
                    filter-properties
                "
            >
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <export-to-excel-button
                :href="exportExcelHref"
            ></export-to-excel-button>
            <exportToPDFButton
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation.toLowerCase()"
            />
            <responsive-dropdown
                :icon="['fas', 'file']"
                :items="pageOrientationOptions"
                variant="primary"
            >
                {{ pageOrientation }}
            </responsive-dropdown>
            <b-button-group class="d-lg-none">
                <more-filters-dropdown>
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <form-search-multiselect
                            v-model="filterProperties"
                            :options="filterPropertyOptions"
                            :icon="['fas', 'hotel']"
                            variant="primary"
                            placeholder="Filter Properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal
                class="ml-1 ml-md-0"
                :modalId="reportSettingsModalId"
                :initialValues="reportSettings"
            />
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    ALL_DATES,
    THIS_WEEK,
    THIS_YEAR,
    LAST_WEEK,
    LAST_YEAR,
} from '../generic/date-range-picker/date-range-picker.vue';
import { REPORT_DISCOUNTS_ENDPOINT } from '../../endpoints';
import ReportSummaryBar from './report-summary-bar.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { PAGE_ORIENTATION_LABELS } from '../../constants';
import { toAmountDisplay } from '../../utils/numbers';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    MULTI_SELECT_INPUT,
    SELECT_INPUT,
} from './report-settings/report-settings-modal.vue';

const exportToPDFButton = () =>
    import('../generic/export-to-pdf-button/export-to-pdf-button.vue');

export default {
    name: 'DiscountReport',
    components: {
        Page,
        Grid,
        ResponsiveDropdown,
        DateRangePicker,
        FormSearchMultiselect,
        MoreFiltersDropdown,
        reportSummaryBar: ReportSummaryBar,
        exportToPDFButton,
        ExportToExcelButton,
        ReportSettingsModal,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Discount Report',
            tabs: [
                {
                    title: 'Discount Report',
                    route: '/app/reports/discounts/',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_YEAR),
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                LAST_WEEK,
                THIS_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            dateRequestFormat: 'yyyy-MM-dd',
            busy: false,
            gridApi: null,
            rowData: [],
            getRowStyle: null,
            discountTotal: 0,
            windowWidth: 0,
            windowHeight: 0,
            filterProperties: [],
            filterPropertyOptions: [],
            pageOrientation: PAGE_ORIENTATION_LABELS[0],
            sortByProperty: '',
            statusBar: null,
            pdfCacheKey: null,
            reportSettingsModalId: 'report-settings-modal-id',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        columnDefs() {
            return [
                {
                    headerName: '#',
                    field: 'number',
                    valueGetter: (params) => {
                        return params.node.rowPinned
                            ? this.rowData.length
                            : params.node.rowIndex + 1;
                    },
                    maxWidth: 50,
                    pinned: 'left',
                    autoWidthExclude: true,
                },
                {
                    field: 'invoice_id',
                    headerName: 'Inv #',
                    minWidth: 80,
                    pinned: 'left',
                },
                {
                    field: 'to_date',
                    headerName: 'Inv End',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'from_date',
                    headerName: 'Inv Start',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'created',
                    headerName: 'Booking Date',
                    type: 'dateColumn',
                    minWidth: 100,
                },
                {
                    field: 'property',
                    headerName: 'Unit',
                    minWidth: 80,
                },
                {
                    field: 'first_name',
                    headerName: 'First Name',
                    minWidth: 80,
                },
                {
                    field: 'last_name',
                    headerName: 'Last Name',
                    minWidth: 80,
                },
                {
                    field: 'dtype',
                    headerName: 'Discount Type',
                    minWidth: 80,
                },
                {
                    field: 'type',
                    headerName: 'Value Type',
                    minWidth: 80,
                },
                {
                    field: 'discount',
                    headerName: 'Discount',
                    minWidth: 80,
                },
                {
                    field: 'discount_total',
                    headerName: 'Discount Amt',
                    type: 'amountColumn',
                    minWidth: 100,
                },
                {
                    field: 'coupon_code',
                    headerName: 'Coupon Code',
                    minWidth: 200,
                    autoWidthExclude: true,
                },
            ];
        },
        pinnedBottomRowData() {
            return [
                {
                    discount_total: this.discountTotal || 0,
                },
            ];
        },
        exportPdfHref() {
            let url = '/app/reports/discounts/row-data/pdf/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&sort_by_property=${this.sortByProperty}`;
            return url;
        },
        exportExcelHref() {
            let url = '/app/reports/discounts/row-data/excel/';
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&properties=${this.filterProperties.join(',')}`;
            url += `&sort_by_property=${this.sortByProperty}`;
            return url;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        pageOrientationOptions() {
            return [
                {
                    label: PAGE_ORIENTATION_LABELS[0],
                    click: () => {
                        this.pageOrientation = PAGE_ORIENTATION_LABELS[0];
                    },
                },
                {
                    label: PAGE_ORIENTATION_LABELS[1],
                    click: () => {
                        this.pageOrientation = PAGE_ORIENTATION_LABELS[1];
                    },
                },
            ];
        },
        isMobile() {
            const md = 768;
            return this.windowWidth < md || this.windowHeight < md;
        },
        reportSettings() {
            return {
                name: 'Discounts Report',
                report_sysname: 'DISCOUNTS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        label: 'Date Range',
                        name: 'date_range',
                        predefinedRanges: this.predefinedRanges
                    },
                    {
                        type: MULTI_SELECT_INPUT,
                        options: this.filterPropertyOptions,
                        name: 'properties',
                        value: [],
                        label: 'Only for Properties',
                        placeholder: 'All Properties'
                    },
                    {
                        type: SELECT_INPUT,
                        options: [
                            {
                                value: 'asc',
                                text: 'Ascending',
                            },
                            {
                                value: 'desc',
                                text: 'Descending',
                            },
                        ],
                        value: 'asc',
                        name: 'sort_by_property',
                        label: 'Sort By Property',
                    },
                ],
            };
        },
    },
    mounted() {
        this.fetchProperties();
        this.fetchData();
        const grid = this.$refs.grid;
        this.gridApi = grid.gridOptions.api;
        this.onResize();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    beforeMount() {
        this.getRowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        this.statusBar = {
            statusPanels: [
                {
                    key: 'reportSummaryBar',
                    statusPanel: 'reportSummaryBar',
                    align: 'left',
                },
            ],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                this.sortByProperty = '';
                const { data } = await API.get(REPORT_DISCOUNTS_ENDPOINT(), {
                    params: {
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        properties: this.filterProperties.join(','),
                        sort_by_property: this.sortByProperty,
                    },
                });
                const { discounts, total, pdf_cache_key } = data;
                this.pdfCacheKey = pdf_cache_key;
                // get rowData
                this.rowData = discounts.map((discount, index) => ({
                    ...discount,
                    id: index,
                }));
                this.discountTotal = total;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchProperties() {
            const { data } = await API.get(
                '/app/reports/discounts/properties/'
            );
            this.filterPropertyOptions = data.map((item) => ({
                value: item.property_id,
                text: item.name,
                disabled: item.disabled,
            }));
        },
        onResize() {
            this.windowWidth = window.innerWidth;
            this.windowHeight = window.innerHeight;
            const statusBarComponent =
                this.gridApi.getStatusPanel('reportSummaryBar');
            let componentInstance = statusBarComponent;
            if (statusBarComponent.getFrameworkComponentInstance) {
                componentInstance =
                    statusBarComponent.getFrameworkComponentInstance();
            }
            componentInstance.setVisible(this.isMobile);
            componentInstance.setTitle(this.title);
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
        sortChanged({ columnApi }) {
            const sortState = columnApi.getColumnState();
            const propertySortState = sortState.filter(
                (state) => state.colId === 'property'
            );
            if (sortState.length > 0 && propertySortState.length === 1) {
                this.sortByProperty = propertySortState[0].sort;
            } else {
                this.sortByProperty = '';
            }
        },
    },
    watch: {
        filterProperties: {
            deep: true,
            handler() {
                this.fetchData();
            },
        },
    },
};
</script>

<style lang="scss">
.discounts-report {
    .filter-properties {
        > fieldset {
            margin: 0 !important;
        }
    }
    .sort-by-property {
        width: 140px;
    }
}
</style>